import {ChangeDetectionStrategy, Component, Input, OnInit, QueryList, ViewChild, ViewChildren} from "@angular/core";
import {
	AccreditationBody,
	AttachedQuestion,
	PriceFactor,
	Product,
	StandardName
} from "../../../../../store/state/order.state";
import {ChangeSet, FeatureToHideInHistoryView} from "../../../history/order-history.model";
import {NbPosition, NbTooltipDirective, NbTrigger} from "@nebular/theme";
import {TranslationsHelperService} from "../../../../translations/translations-helper.service";
import {AuthenticationService} from "../../../../../authentication/authentication.service";

@Component({
    selector: 'price-factors-details',
    templateUrl: './price-factors-details.template.html',
    styleUrls: ['./price-factors-details.style.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PriceFactorsDetailsComponent implements OnInit {

	readonly RANDOM_NUMBER: number = Math.random();
	readonly noop: NbTrigger = NbTrigger.NOOP;
	readonly hover: NbTrigger = NbTrigger.HOVER;
	readonly nbStart: NbPosition = NbPosition.START;

	totalModifierText: string;

	constructor(public translationsHelper: TranslationsHelperService, private authService: AuthenticationService) {
	}

	ngOnInit(): void {
		this.totalModifierText = '‘Selected’ calculates the total percentage of increases and decreases based on the client’s' +
			'responses for each product and the risk classification based on the EAF code /matrix.' +
			'\n‘Applied’ displays the final applied price factor, taking into account the maximum allowable discount of 30%. ' +
			'\nA ‘-’ indicates a decrease, while a ‘+’ indicates an increase. \nThis overview is available for '
			+ (this.product.accreditationBody === AccreditationBody.ANAB ? '9001, 14001, and 45001' : '9001, 14001, 45001, 27001');
    }

	@Input() product: Product;
	@Input() changeSet: ChangeSet;	// optional
	@Input() translateClientsData;
	@Input() showTooltips: boolean = false;
	@Input() useOriginalTranslations: boolean;
	@ViewChildren('tooltipPriceFactors') tooltipPriceFactors: QueryList<NbTooltipDirective>;
	@ViewChild('tooltipModifiersInfo') tooltipModifiersInfo: NbTooltipDirective;


	standardName() {
		return this.product.standard.name;
	}

	isPriceFactorChanged(priceFactor: PriceFactor): boolean {
		return !!this.changeSet && !!this.changeSet.changes.productChanges[this.product.standard.name] &&
			!!this.changeSet.changes.productChanges[this.product.standard.name].priceFactors[priceFactor.type];
	}

	percentageFactors(priceFactor: PriceFactor): string {
		return `Yes: ${priceFactor.positiveAnswerFactor}% | No: ${priceFactor.negativeAnswerFactor}%`;
	}

	showTooltipIfAllowed(index: number) {
		this.showTooltips && this.tooltipPriceFactors.find((item, i) => index == i).show();
	}

	hideTooltip(index: number) {
		this.showTooltips && this.tooltipPriceFactors.find((item, i) => index == i).hide();
	}

	getTranslationSubjectForPriceFactorQuestion(priceFactor: PriceFactor, attachedQuestion: AttachedQuestion) {
		return `priceFactor[${priceFactor.type}][${attachedQuestion.type}]`;
	}

	shouldShowTotalModifiers(): boolean {
		if (this.isClientUser()) return false;
		if (this.isHiddenByFeature()) return false;
		if (!this.hasValidDiscounts()) return false;
		return this.isStandardAllowed();
	}

	private isHiddenByFeature(): boolean {
		return this.changeSet?.hideInHistoryView.includes(FeatureToHideInHistoryView.PRICE_FACTOR_INDICATORS) ?? false;
	}

	private isClientUser(): boolean {
		return this.authService.isLoggedUserClient(true);
	}

	private hasValidDiscounts(): boolean {
		const { calculatedDiscount, appliedDiscount } = this.product.pricing;
		return calculatedDiscount !== null && appliedDiscount !== null;
	}

	private isStandardAllowed(): boolean {
		const standardName = this.product.standard.name;
		const isAnab = this.product.accreditationBody === AccreditationBody.ANAB;

		const allowedStandards = isAnab
			? [StandardName.ISO_9001, StandardName.ISO_14001, StandardName.ISO_45001]
			: [StandardName.ISO_9001, StandardName.ISO_14001, StandardName.ISO_45001, StandardName.ISO_27001];

		return allowedStandards.includes(standardName);
	}

	getCalculatedDiscount() {
		return this.product.pricing.calculatedDiscount * 100;
	}

	getAppliedDiscount() {
		return this.product.pricing.appliedDiscount * 100;
	}
}
